import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowRight, CalendarClock, Inbox, Loader2,
  MapPin, RefreshCw, Send, Stethoscope, Zap,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForceOfferModal = ({ open, onClose, onConfirm, referral, busy }) => {
  const [clinicianId, setClinicianId] = useState('');
  useEffect(() => { if (open) setClinicianId(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="spine-force-offer-modal">
        <DialogHeader>
          <DialogTitle>Force offer to clinician</DialogTitle>
          <DialogDescription>
            Bypass the matcher and push this referral directly to a specific clinician. The referral will return to <b>matching</b>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Clinician ID</Label>
            <Input
              value={clinicianId}
              onChange={(e) => setClinicianId(e.target.value)}
              placeholder="user-uuid"
              className="mt-1.5 h-11 rounded-xl"
              data-testid="spine-force-clinician-id"
            />
          </div>
          <div className="text-xs text-cn-text-muted">
            Referral: <code className="text-[11px]">#{(referral?.id || '').slice(0, 8)}</code>
            {referral?.state && <> · State <b>{referral.state}</b></>}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="cn-btn flex-1 justify-center" data-testid="spine-force-cancel">Cancel</button>
            <button
              disabled={busy || !clinicianId}
              onClick={() => onConfirm(referral, clinicianId)}
              className="cn-btn cn-btn-primary flex-1 justify-center"
              data-testid="spine-force-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Push offer <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EscalatedCard = ({ r, onForce }) => (
  <div className="cn-card rounded-2xl p-5 space-y-2 border-l-4 border-red-400" data-testid={`spine-escalated-${r.id}`}>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-800">
        <AlertTriangle className="w-3 h-3" /> Escalated
      </span>
      <span className="text-[11px] uppercase font-semibold px-2 py-0.5 rounded-full bg-cn-soft text-cn-primary">{r.urgency}</span>
      <span className="text-xs text-cn-text-muted inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.state}</span>
      {r.discharge_date && <span className="text-xs text-cn-text-muted inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {r.discharge_date}</span>}
    </div>
    <div className="text-sm text-cn-text font-medium">{(r.ordered_services || []).join(', ')}</div>
    <div className="text-xs text-cn-text-muted">
      Patient #{(r.patient_id || '').slice(0, 8)} · {(r.diagnosis_codes || []).join(', ')}
    </div>
    {r.escalation_reason && (
      <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1 inline-block">
        Reason: <b>{r.escalation_reason}</b>
      </div>
    )}
    <div className="text-[11px] text-cn-text-muted/80">
      Escalated at {r.escalated_at ? new Date(r.escalated_at).toLocaleString() : '—'}
    </div>
    <div className="pt-2 border-t border-cn-border">
      <button
        onClick={() => onForce(r)}
        className="cn-btn cn-btn-primary"
        data-testid={`spine-force-offer-btn-${r.id}`}
      >
        <Send className="w-4 h-4" /> Force offer to clinician
      </button>
    </div>
  </div>
);

export default function SpineAdminEscalations() {
  const { getAuthHeader } = useAuth();
  const [queue, setQueue] = useState({ count: 0, rows: [] });
  const [loading, setLoading] = useState(true);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [forceTarget, setForceTarget] = useState(null);
  const [forceBusy, setForceBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/spine/placements/escalations/queue`, getAuthHeader());
      setQueue(res.data || { count: 0, rows: [] });
    } catch (e) {
      toast.error('Failed to load escalation queue');
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const runSweep = async () => {
    setSweepBusy(true);
    try {
      const res = await axios.post(`${API}/spine/sla-sweep/tick`, {}, getAuthHeader());
      toast.success(`Sweep complete — expired ${res.data?.expired || 0}, cascaded ${res.data?.cascaded || 0}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally { setSweepBusy(false); }
  };

  const confirmForce = async (r, clinician_id) => {
    setForceBusy(true);
    try {
      await axios.post(`${API}/spine/placements/offer`, {
        referral_id: r.id, clinician_id,
      }, getAuthHeader());
      toast.success('Offer pushed — referral returned to matching');
      setForceTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not push offer');
    } finally { setForceBusy(false); }
  };

  return (
    <DashboardShell
      title="Escalation queue"
      subtitle="Referrals where the orchestrator exhausted candidates or breached SLA. Manual override only."
      navItems={[]}
      testId="spine-admin-escalations"
      rightSlot={
        <div className="flex items-center gap-2">
          <button onClick={load} className="cn-btn !bg-white !text-cn-primary !border !border-cn-border" data-testid="spine-escalations-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            disabled={sweepBusy}
            onClick={runSweep}
            className="cn-btn cn-btn-primary"
            data-testid="spine-sweep-tick-btn"
          >
            {sweepBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4" /> Run SLA sweep</>}
          </button>
        </div>
      }
    >
      <div className="cn-card rounded-2xl p-5 mb-6 flex items-center justify-between" data-testid="spine-escalation-summary">
        <div>
          <div className="text-3xl font-display font-semibold text-cn-text">{queue.count}</div>
          <div className="text-sm text-cn-text-muted">referrals stuck · need ops intervention</div>
        </div>
        <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">
          <AlertTriangle className="w-7 h-7 text-red-600" />
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div>
      ) : queue.count === 0 ? (
        <div className="cn-card rounded-3xl p-10 text-center" data-testid="spine-escalations-empty">
          <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-7 h-7 text-emerald-700" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text mb-2">All clear</h3>
          <p className="text-cn-text-muted max-w-md mx-auto">
            No referrals in the escalation queue. The orchestrator is healthy.
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="spine-escalations-list">
          {queue.rows.map((r) => <EscalatedCard key={r.id} r={r} onForce={setForceTarget} />)}
        </div>
      )}

      <ForceOfferModal
        open={!!forceTarget}
        onClose={() => setForceTarget(null)}
        onConfirm={confirmForce}
        referral={forceTarget}
        busy={forceBusy}
      />
    </DashboardShell>
  );
}
